import axios from 'axios';

/**
 * Mock service to simulate HTTP status code errors for API endpoints
 *
 * Usage:
 * 1. Enable mocking: localStorage.setItem('mockApiError', 'true')
 * 2. Disable mocking: localStorage.removeItem('mockApiError')
 *
 * Or set VUE_APP_MOCK_API_ERROR=true in .env file
 *
 * To mock specific endpoints, use:
 * localStorage.setItem('mockApiErrorEndpoints', JSON.stringify(['/homepage/middle-nav-icons', '/homepage/featured']))
 *
 * To set a custom status code (default is 429):
 * localStorage.setItem('mockApiErrorStatusCode', '500') // Mock 500 errors
 * Or set VUE_APP_MOCK_STATUS_CODE=500 in .env file
 */

let isMockingEnabled = false;
let requestInterceptorId = null;
let mockEndpoints = [];
let mockStatusCode = 429; // Default to 429 for backward compatibility

// Public API endpoints that should never be mocked
// These endpoints don't require authentication and should work normally
const EXCLUDED_PUBLIC_PATHS = ['/featureflag/app-flags'];

/**
 * Get the status code to mock
 */
function getMockStatusCode() {
  if (typeof window === 'undefined') return mockStatusCode;

  // Check localStorage first
  const storedStatusCode = localStorage.getItem('mockApiErrorStatusCode');
  if (storedStatusCode) {
    const code = parseInt(storedStatusCode, 10);
    if (!isNaN(code) && code >= 100 && code < 600) {
      return code;
    }
  }

  // Check environment variable
  const envStatusCode = process.env.VUE_APP_MOCK_STATUS_CODE;
  if (envStatusCode) {
    const code = parseInt(envStatusCode, 10);
    if (!isNaN(code) && code >= 100 && code < 600) {
      return code;
    }
  }

  // Default to 429
  return 429;
}

/**
 * Get status text for a given status code
 */
function getStatusText(statusCode) {
  const statusTexts = {
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    408: 'Request Timeout',
    429: 'Too Many Requests',
    500: 'Internal Server Error',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
    504: 'Gateway Timeout',
  };
  return statusTexts[statusCode] || 'Error';
}

/**
 * Check if mocking should be enabled
 */
function shouldMock() {
  if (typeof window === 'undefined') return false;

  // Check localStorage
  const localStorageMock = localStorage.getItem('mockApiError');
  if (localStorageMock === 'true') return true;

  // Check environment variable
  return process.env.VUE_APP_MOCK_API_ERROR === 'true';
}

/**
 * Get list of endpoints to mock (from localStorage or default to all)
 */
function getMockEndpoints() {
  if (typeof window === 'undefined') return [];

  const storedEndpoints = localStorage.getItem('mockApiErrorEndpoints');
  if (storedEndpoints) {
    try {
      return JSON.parse(storedEndpoints);
    } catch (e) {
      console.warn('[MOCK API] Failed to parse mockApiErrorEndpoints:', e);
    }
  }

  // Default: mock all API endpoints
  return null; // null means mock all
}

/**
 * Check if a URL should be mocked
 */
function shouldMockUrl(url) {
  if (!url) return false;

  const apiUrl = process.env.VUE_APP_API_URL || '';
  if (!url.includes(apiUrl)) return false;

  // Never mock public API endpoints that don't require authentication
  if (EXCLUDED_PUBLIC_PATHS.some(path => url.includes(path))) {
    return false;
  }

  // If no specific endpoints configured, mock all
  if (!mockEndpoints || mockEndpoints.length === 0) {
    return true;
  }

  // Check if URL matches any of the configured endpoints
  return mockEndpoints.some(endpoint => url.includes(endpoint));
}

/**
 * Create a mock error response with the specified status code
 */
function createMockError(config, statusCode) {
  const statusText = getStatusText(statusCode);
  const error = new Error(`Mock ${statusCode} error (${statusText})`);
  error.response = {
    status: statusCode,
    statusText: statusText,
    headers: {},
    data: {
      message: `Mock ${statusCode} error`,
      error: statusText,
    },
  };

  // Add retry-after header for 429 errors
  if (statusCode === 429) {
    error.response.headers['retry-after'] = '1';
  }

  error.config = config;
  error.isAxiosError = true;
  return error;
}

/**
 * Mock axios request interceptor - intercepts requests and returns mock errors
 */
function mockAxiosRequestInterceptor(config) {
  const url = config.url || '';

  if (!shouldMock() || !shouldMockUrl(url)) {
    return config;
  }

  const method = config.method?.toLowerCase() || 'get';
  const statusCode = getMockStatusCode();
  console.warn(`[MOCK API] ⚠️ Simulating ${statusCode} error for ${method.toUpperCase()} ${url}`);

  // Create a custom adapter that returns mock error
  config.adapter = () => Promise.reject(createMockError(config, statusCode));

  return config;
}

/**
 * Initialize mock service
 */
export function initMockApiError() {
  // Remove existing interceptors if any
  if (requestInterceptorId !== null) {
    axios.interceptors.request.eject(requestInterceptorId);
    requestInterceptorId = null;
  }

  if (shouldMock()) {
    mockEndpoints = getMockEndpoints();
    mockStatusCode = getMockStatusCode();
    console.warn('[MOCK API] ⚠️ Mock API enabled', {
      statusCode: mockStatusCode,
      endpoints: mockEndpoints || 'ALL',
    });

    // Use request interceptor to catch requests before they're sent
    requestInterceptorId = axios.interceptors.request.use(
      config => mockAxiosRequestInterceptor(config),
      error => Promise.reject(error),
    );

    isMockingEnabled = true;
  } else {
    // Mock API disabled - no log needed unless debugging
    isMockingEnabled = false;
  }
}

/**
 * Enable mocking programmatically
 * @param {number} statusCode - HTTP status code to mock (default: 429)
 * @param {Array<string>} endpoints - Specific endpoints to mock (default: all)
 */
export function enableMocking(statusCode = 429, endpoints = null) {
  if (typeof window !== 'undefined') {
    localStorage.setItem('mockApiError', 'true');
    localStorage.setItem('mockApiErrorStatusCode', String(statusCode));
    if (endpoints && Array.isArray(endpoints)) {
      localStorage.setItem('mockApiErrorEndpoints', JSON.stringify(endpoints));
    }
    initMockApiError();
  }
}

/**
 * Disable mocking programmatically
 */
export function disableMocking() {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('mockApiError');
    localStorage.removeItem('mockApiErrorEndpoints');
    localStorage.removeItem('mockApiErrorStatusCode');
    initMockApiError();
  }
}

/**
 * Check if mocking is currently enabled
 */
export function isMockingActive() {
  return isMockingEnabled;
}
